import { Button, Column, Dialog, DialogActions, DialogContent, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';

/**
 * Matches its sibling `ReferralAccountExistsDialog` — same Figma frame, so the
 * two must not drift apart visually. See that file for why the width is
 * overridden rather than taken from a `size` variant.
 */
const PANEL_MAX_WIDTH = 358;
const PANEL_STYLE = { maxWidth: PANEL_MAX_WIDTH } as const;

/**
 * `Dialog` hardcodes `aria-labelledby` to the id it would put on a *string*
 * child of `DialogTitle`. The heading lives in the content instead, so this id
 * points the label at it — `Dialog` spreads its props after its own attributes,
 * so passing `aria-labelledby` overrides the otherwise dangling default.
 */
const TITLE_ID = 'referral-not-eligible-title';

export interface IReferralNotEligibleDialogProps {
  /** Whether the dialog is visible. */
  open: boolean;
  /** "Close", the backdrop and Escape. */
  onClose: () => void;
}

/**
 * The "you're not eligible for this referral" dialog (GOTU-7434, Figma
 * 4608-25088), mirroring mobile's `ReferralNotEligibleDialog` (GOTU-7313).
 *
 * Shown when an **already signed-in** office opens a v2 referral link. The
 * referral is for new accounts only, so there is nothing to apply and nowhere
 * to send them: the one action closes it and leaves them on the dashboard.
 *
 * Its sibling `ReferralAccountExistsDialog` covers the other half of the same
 * Figma frame — someone signing up whose email already has an account. That one
 * tells them to log in; this one must not, because they already are. Pointing
 * both paths at a single dialog is exactly the bug this split fixes.
 *
 * Presentational only; `Dashboard` renders it off the navigation state
 * `ReferralLinkRedirect` sets.
 *
 * Copy is mobile's verbatim. The two clients must not diverge in wording or
 * outcome, so change both or neither.
 */
export const ReferralNotEligibleDialog = ({
  open,
  onClose,
}: IReferralNotEligibleDialogProps) => (
  <Dialog
    open={open}
    onClose={onClose}
    size="medium"
    style={PANEL_STYLE}
    aria-labelledby={TITLE_ID}
    data-testid="referral-not-eligible-dialog"
  >
    {/* `DialogContent` brings `px-4 pb-5` but no top padding of its own — the
        DS expects a `DialogTitle` above it. Set here instead, to balance
        `pb-5`, rather than adding an empty title row. */}
    <DialogContent>
      <Column gap={4} align="center" pt={6}>
        <Typography id={TITLE_ID} bold variant="subtitle1" textAlign="center">
          You&rsquo;re not eligible for this referral
        </Typography>
        <Typography variant="body1" textAlign="center" color={colors.neutral[500]}>
          You&rsquo;re already signed in to your GoTu account. You can still refer other pros and
          earn rewards yourself.
        </Typography>
      </Column>
    </DialogContent>
    {/* `DialogActions` is `justify-end` by default; a single acknowledging
        action centres. */}
    <DialogActions className="justify-center">
      <Button
        data-cy="referral-not-eligible-close-button"
        data-testid="referral-not-eligible-close-button"
        onClick={onClose}
      >
        Close
      </Button>
    </DialogActions>
  </Dialog>
);
